import { useEffect, useState } from "react";

import { Badge, Infobulle, Jauge, cn } from "@/shared/ui";
import { Clock, Loader2 } from "@/shared/ui/icons";

export type FlowRunBadgeSize = "sm" | "md";

export interface FlowRunBadgeProps {
  /** Nom du flow en cours (tronqué si trop long). */
  flowName: string;
  /** Nœuds terminés. */
  completed: number;
  /** Nœuds totaux. */
  total: number;
  /** Progression 0–100 (clampée). */
  percent: number;
  /** `sm` = carte conversation (dense). `md` = barre du chat (plus lisible). */
  size?: FlowRunBadgeSize;
  /** Classes layout additionnelles sur le conteneur (ex: `flex-1` dans le chat). */
  className?: string;
  /** ISO : depuis quand le run est au step COURANT. Si fourni, affiche un
   * compteur live « ⏱ Xh » → permet de repérer un fan bloqué à un step. */
  enteredAt?: string | null;
  /** Étiré sur toute sa rangée (la ligne de la pile) : le nom prend la place
   * qui reste, l'horloge va au bout à droite — là où la ligne met déjà
   * « depuis quand ». Sans lui, le badge est compact (la carte) et le nom se
   * coupe à 9 rem. */
  etire?: boolean;
}

/** Formate l'âge du step courant en libellé compact FR (live). null si invalide. */
function formatStepAge(iso: string, nowMs: number): string | null {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const min = Math.floor((nowMs - t) / 60000);
  if (min < 0) return null;
  if (min < 1) return "à l'instant";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
  const d = Math.floor(h / 24);
  const hr = h % 24;
  return hr ? `${d}j${hr}h` : `${d}j`;
}

/** Compteur live « depuis X » du step courant (re-render toutes les 30s). */
function StepAge({ enteredAt, size }: { enteredAt: string; size: FlowRunBadgeSize }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const label = formatStepAge(enteredAt, now);
  if (!label) return null;
  return (
    <Infobulle texte={`À ce step depuis ${label}`}>
      <span
        className={cn(
          "flex shrink-0 items-center gap-1 tabular-nums leading-none text-faint",
          size === "sm" ? "text-[10.5px]" : "text-[11.5px]",
        )}
      >
        <Clock aria-hidden className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} />
        {label}
      </span>
    </Infobulle>
  );
}

/**
 * Badge « flow en cours » — un `Badge` (la pilule : le spinner, le nom du flow,
 * le pourcentage) et une `Jauge` dessous qui dit où en est le fan. Rien n'y est
 * dessiné à la main : la pastille et la barre sont celles de la librairie
 * (Kayla, 01-11 : « c'est pas le nouveau thème »). Ton `gris` : un flow qui
 * tourne n'est ni validé, ni en alerte — c'est une information, pas un statut.
 *
 * Partagé entre la carte conversation (`ConversationsPanel`, `size="sm"`), la
 * ligne de la pile (`LigneDeLaPile`, `size="sm"` étiré sur sa rangée) et la
 * barre de statut du chat (`ConvStatusBar`, `size="md"`) — un seul rendu pour
 * que les trois contextes restent strictement identiques.
 *
 * 📏 Le POURCENTAGE, pas la fraction `n/N`. Kayla, 12-09-2026 : « au lieu de
 * dire à quelle étape, on peut dire à quel pourcentage. C'est peut-être plus
 * facile pour l'affichage, ça prendra peut-être moins de place. » « 3/7 »
 * demandait de savoir combien d'étapes a le flow ; « 43 % » se lit seul, et
 * tient en trois caractères quelle que soit la taille du flow. La fraction
 * reste au survol.
 */
export function FlowRunBadge({
  flowName,
  completed,
  total,
  percent,
  size = "sm",
  className = "",
  enteredAt = null,
  etire = false,
}: FlowRunBadgeProps) {
  const pct = Math.min(100, Math.max(0, percent ?? 0));
  const sm = size === "sm";
  return (
    <Infobulle texte={`${flowName} — ${completed}/${total}`}>
      <div className={cn("flex min-w-0 flex-col gap-1", sm && !etire && "shrink-0", etire && "w-full", className)}>
        <div className="flex min-w-0 items-center gap-2">
          <Badge
            ton="gris"
            size={sm ? "sm" : "md"}
            className="min-w-0 max-w-full"
            icon={<Loader2 aria-hidden className="animate-spin" />}
          >
            <span className={cn("min-w-0 truncate text-fg", sm && !etire && "max-w-[9rem]")}>{flowName}</span>
            <span className="shrink-0 tabular-nums">{Math.round(pct)}&nbsp;%</span>
          </Badge>
          {enteredAt ? (
            <span className={cn("flex min-w-0", etire && "ml-auto")}>
              <StepAge enteredAt={enteredAt} size={size} />
            </span>
          ) : null}
        </div>
        <Jauge valeur={pct} taille="sm" ariaLabel={flowName} />
      </div>
    </Infobulle>
  );
}
